// src/components/common/NewsletterPopup.jsx
import React, { useEffect, useState, useRef } from "react";
import { X, ChevronDown } from "lucide-react";
import Indian from "../../assets/indian.png";

const SESSION_KEY = "aishcraft_newsletter_popup_shown";
const SHOW_AFTER_MS = 3000; // 3 second baad popup dikhega

const COUNTRIES = [
  { name: "India", code: "+91", flag: Indian, isImage: true },
  { name: "United States", code: "+1", flag: "🇺🇸", isImage: false },
  { name: "United Kingdom", code: "+44", flag: "🇬🇧", isImage: false },
  { name: "Canada", code: "+1", flag: "🇨🇦", isImage: false },
  { name: "Australia", code: "+61", flag: "🇦🇺", isImage: false },
  { name: "United Arab Emirates", code: "+971", flag: "🇦🇪", isImage: false },
  { name: "Singapore", code: "+65", flag: "🇸🇬", isImage: false },
];

export default function NewsletterPopup() {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ firstName: "", email: "", phone: "" });
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState("");

  const [country, setCountry] = useState(COUNTRIES[0]); // default India
  const [countryOpen, setCountryOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setOpen(true);
    }, SHOW_AFTER_MS);
    return () => clearTimeout(timer);
  }, []);

  // Dropdown ke bahar click hone par close ho jaye
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setCountryOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const close = () => setOpen(false);

  const selectCountry = (c) => {
    setCountry(c);
    setCountryOpen(false);
    setErrors((er) => ({ ...er, phone: "" }));
  };

  // Sirf letters aur space allow — numbers/symbols hata do
  const handleNameChange = (e) => {
    const val = e.target.value.replace(/[^a-zA-Z\s]/g, "");
    set("firstName", val);
    setErrors((er) => ({ ...er, firstName: "" }));
  };

  const handleEmailChange = (e) => {
    set("email", e.target.value);
    setErrors((er) => ({ ...er, email: "" }));
  };

  // Sirf digits allow, max 10 digit
  const handlePhoneChange = (e) => {
    const digitsOnly = e.target.value.replace(/\D/g, "").slice(0, 10);
    set("phone", digitsOnly);
    setErrors((er) => ({ ...er, phone: "" }));
  };

  const validate = () => {
    const newErrors = {};

    if (!form.firstName.trim()) {
      newErrors.firstName = "Name is required";
    } else if (!/^[a-zA-Z\s]+$/.test(form.firstName.trim())) {
      newErrors.firstName = "Name should contain only letters";
    }

    const emailTrimmed = form.email.trim();
    if (!emailTrimmed) {
      newErrors.email = "Email is required";
    } else if (!/^[a-zA-Z0-9._%+-]+@gmail\.com$/.test(emailTrimmed)) {
      newErrors.email = "Please enter a valid Gmail address";
    }

    if (form.phone && form.phone.length !== 10) {
      newErrors.phone = "Phone number must be 10 digits";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };



const handleSubmit = async (e) => {
  e.preventDefault();
  if (!validate()) return;

  try {
    const res = await fetch("https://aishcraft.com/backend/newsletter/subscribe.php", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        firstName: form.firstName.trim(),
        email: form.email.trim(),
        phone: form.phone,
        countryCode: country.code,
        source: "Popup",
      }),
    });

    const result = await res.json();

    if (!res.ok || !result.success) {
      setSubmitError(result.message || "Something went wrong");
      return;
    }

    setSubmitError("");
    setSubmitted(true);
    setTimeout(close, 1800);
  } catch (err) {
    setSubmitError("Network error, please try again");
  }
};
  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[999] flex items-center justify-center bg-black/50 px-4"
      onClick={close}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-[420px] sm:max-w-[440px] bg-white rounded-2xl shadow-2xl px-5 py-7 xs:px-7 xs:py-8 sm:px-9 sm:py-10 max-h-[92vh] overflow-y-auto"
      >
        {/* Close button */}
        <button
          onClick={close}
          aria-label="Close"
          className="absolute top-3.5 right-3.5 xs:top-4 xs:right-4 text-gray-500 hover:text-black transition-colors"
        >
          <X size={20} />
        </button>

        {submitted ? (
          <div className="py-8 text-center">
            <p className="text-2xl mb-2">🎉</p>
            <h3 className="text-lg xs:text-xl font-extrabold text-black mb-1.5">You're in!</h3>
            <p className="text-[13px] xs:text-sm text-gray-600 font-semibold">Check your inbox for your 10% off code.</p>
          </div>
        ) : (
          <>
            {/* Heading */}
            <h2 className="text-center text-[20px] xs:text-[24px] sm:text-[26px] font-extrabold text-black leading-snug mb-2.5 xs:mb-3 px-1 xs:px-4">
              10% off one-time purchase products
            </h2>
            <p className="text-center text-[13px] xs:text-[14px] text-gray-700 font-medium leading-relaxed mb-5 xs:mb-6 px-1 xs:px-3">
              Enjoy special discounts, exclusive designs, journaling tips and more in your inbox
            </p>

            <form onSubmit={handleSubmit} className="flex flex-col gap-3 xs:gap-3.5" noValidate>
              <div>
                <input
                  type="text"
                  placeholder="First name"
                  value={form.firstName}
                  onChange={handleNameChange}
                  required
                  className={`w-full rounded-lg border px-4 py-3 text-[14px] font-medium text-black placeholder-gray-400 outline-none transition-colors ${
                    errors.firstName ? "border-red-500" : "border-gray-300 focus:border-blue-500"
                  }`}
                />
                {errors.firstName && (
                  <p className="text-[11px] text-red-500 font-medium mt-1 px-1">{errors.firstName}</p>
                )}
              </div>

              <div>
                <input
                  type="email"
                  placeholder="Email"
                  value={form.email}
                  onChange={handleEmailChange}
                  required
                  className={`w-full rounded-lg border px-4 py-3 text-[14px] font-medium text-black placeholder-gray-400 outline-none transition-colors ${
                    errors.email ? "border-red-500" : "border-gray-300 focus:border-blue-500"
                  }`}
                />
                {errors.email && (
                  <p className="text-[11px] text-red-500 font-medium mt-1 px-1">{errors.email}</p>
                )}
              </div>

              {/* Phone row — flag dropdown alag, +91 number box ke andar hi */}
              <div>
                <div className="flex gap-2.5 xs:gap-3 relative" ref={dropdownRef}>
                  {/* Sirf flag + chevron */}
                  <button
                    type="button"
                    onClick={() => setCountryOpen((v) => !v)}
                    className="flex items-center justify-between w-24 rounded-lg border border-gray-300 px-3 py-3 hover:bg-gray-50 transition-colors"
                  >
                    {country.isImage ? (
                      <img src={country.flag} alt={country.name} className="w-10 h-7 xs:w-6 xs:h-6 rounded-none object-cover flex-shrink-0" />
                    ) : (
                      <span className="text-lg leading-none">{country.flag}</span>
                    )}
                    <ChevronDown size={14} className={`text-gray-500 transition-transform ${countryOpen ? "rotate-180" : ""}`} />
                  </button>

                  {countryOpen && (
                    <div className="absolute top-[calc(100%+6px)] left-0 z-10 w-56 max-h-56 overflow-y-auto bg-white border border-gray-200 rounded-lg shadow-lg py-1.5">
                      {COUNTRIES.map((c) => (
                        <button
                          key={c.name}
                          type="button"
                          onClick={() => selectCountry(c)}
                          className="w-full flex items-center gap-2.5 px-3 py-2 text-left text-[13px] font-medium text-black hover:bg-gray-100 transition-colors"
                        >
                          {c.isImage ? (
                            <img src={c.flag} alt={c.name} className="w-8 h-5 rounded-sm object-cover flex-shrink-0" />
                          ) : (
                            <span className="text-base leading-none">{c.flag}</span>
                          )}
                          <span className="flex-1">{c.name}</span>
                          <span className="text-gray-500">{c.code}</span>
                        </button>
                      ))}
                    </div>
                  )}

                  {/* Number box — jiske andar +91 prefix fixed hai, uske baad digits */}
                  <div
                    className={`flex-1 min-w-0 flex items-center rounded-lg border px-4 py-3 transition-colors ${
                      errors.phone ? "border-red-500" : "border-gray-300 focus-within:border-blue-500"
                    }`}
                  >
                    <span className="text-[14px] font-medium text-black mr-1.5 flex-shrink-0">
                      {country.code}
                    </span>
                    <input
                      type="tel"
                      placeholder="10 digit number"
                      value={form.phone}
                      onChange={handlePhoneChange}
                      inputMode="numeric"
                      className="flex-1 min-w-0 text-[14px] font-medium text-black placeholder-gray-400 outline-none border-none p-0 bg-transparent"
                    />
                  </div>
                </div>
                {errors.phone && (
                  <p className="text-[11px] text-red-500 font-medium mt-1 px-1">{errors.phone}</p>
                )}
              </div>

             {submitError && (
  <p className="text-[12px] text-red-500 font-medium text-center -mt-1">{submitError}</p>
)}

<button
  type="submit"
  className="mt-1.5 xs:mt-2 w-full bg-black hover:bg-gray-800 active:scale-[0.98] text-white font-extrabold text-[13px] xs:text-sm tracking-wide py-3.5 rounded-lg transition-all"
>
  GET 10% OFF
</button>
            </form>

            <p className="text-center text-[10.5px] xs:text-[11px] text-gray-500 font-medium leading-relaxed mt-4 xs:mt-5 px-1 xs:px-2">
              By signing up, you agree to receive marketing messages at the phone number or email provided. View our{" "}
              <a href="/privacy-policy" className="underline hover:text-black">privacy policy</a> and{" "}
              <a href="/terms-of-service" className="underline hover:text-black">terms of service</a> for more info.
            </p>
          </>
        )}
      </div>
    </div>
  );
}